import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Warning message interface for component input
 */
export interface WarningMessage {
  message: string;
  severity?: 'warning' | 'error';
}

/**
 * WarningMessageComponent
 *
 * A reusable component for displaying non-blocking validation warnings and errors.
 * - Errors are displayed first in red text
 * - Warnings are displayed after errors with a red "*" and a black
 *   " Suggested Change: " prefix. The words stay black on purpose: these are
 *   non-blocking, and an all-red line reads as an error.
 *
 * Usage:
 * <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
 */
@Component({
  selector: 'app-warning-message',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './warning-message.component.html',
  styleUrl: './warning-message.component.css',
})
export class WarningMessageComponent {
  /**
   * Array of warning messages to display
   * Each warning should have a 'message' property and optional 'severity'
   */
  @Input() warnings: WarningMessage[] = [];

  /**
   * Get messages sorted by severity: errors first, then warnings
   * Each message is displayed on its own line
   */
  getSortedMessages(): WarningMessage[] {
    return [...this.warnings].sort((firstWarning, secondWarning) => {
      // Errors first, then warnings
      const aIsError = firstWarning.severity === 'error';
      const bIsError = secondWarning.severity === 'error';
      if (aIsError && !bIsError) return -1;
      if (!aIsError && bIsError) return 1;
      return 0;
    });
  }
}
